import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface PayrollRecord {
  payroll_id: number
  employee_id: number
  employee_name: string
  pay_period_start: string
  pay_period_end: string
  base_salary: number
  overtime_hours: number
  overtime_rate: number
  overtime_pay: number
  bonuses: number
  deductions: number
  gross_pay: number
  tax_deductions: number
  net_pay: number
  status: 'draft' | 'approved' | 'paid'
  created_at: string
  updated_at: string
}

export interface PayrollState {
  records: PayrollRecord[]
  currentRecord: PayrollRecord | null
  total: number
  isLoading: boolean
  error: string | null
  filters: {
    employee_id?: number
    status?: string
    pay_period?: string
    search?: string
  }
  pagination: {
    page: number
    limit: number
  }
  summary: {
    totalGrossPay: number
    totalNetPay: number
    totalTaxDeductions: number
    totalEmployees: number
    averagePay: number
  }
}

const initialState: PayrollState = {
  records: [],
  currentRecord: null,
  total: 0,
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
  },
  summary: {
    totalGrossPay: 0,
    totalNetPay: 0,
    totalTaxDeductions: 0,
    totalEmployees: 0,
    averagePay: 0,
  },
}

const payrollSlice = createSlice({
  name: 'payroll',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setRecords: (state, action: PayloadAction<{ records: PayrollRecord[]; total: number }>) => {
      state.records = action.payload.records
      state.total = action.payload.total
      state.isLoading = false
      state.error = null
    },
    setCurrentRecord: (state, action: PayloadAction<PayrollRecord | null>) => {
      state.currentRecord = action.payload
    },
    addRecord: (state, action: PayloadAction<PayrollRecord>) => {
      state.records.unshift(action.payload)
      state.total += 1
    },
    updateRecord: (state, action: PayloadAction<PayrollRecord>) => {
      const index = state.records.findIndex(record => record.payroll_id === action.payload.payroll_id)
      if (index !== -1) {
        state.records[index] = action.payload
      }
      if (state.currentRecord?.payroll_id === action.payload.payroll_id) {
        state.currentRecord = action.payload
      }
    },
    removeRecord: (state, action: PayloadAction<number>) => {
      state.records = state.records.filter(record => record.payroll_id !== action.payload)
      state.total -= 1
      if (state.currentRecord?.payroll_id === action.payload) {
        state.currentRecord = null
      }
    },
    setFilters: (state, action: PayloadAction<Partial<PayrollState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setPagination: (state, action: PayloadAction<Partial<PayrollState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setSummary: (state, action: PayloadAction<PayrollState['summary']>) => {
      state.summary = action.payload
    },
    updateSummary: (state, action: PayloadAction<Partial<PayrollState['summary']>>) => {
      state.summary = { ...state.summary, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
    clearCurrentRecord: (state) => {
      state.currentRecord = null
    },
    // Bulk operations
    approveRecords: (state, action: PayloadAction<number[]>) => {
      action.payload.forEach(payrollId => {
        const record = state.records.find(r => r.payroll_id === payrollId)
        if (record) {
          record.status = 'approved'
        }
      })
    },
    markRecordsAsPaid: (state, action: PayloadAction<number[]>) => {
      action.payload.forEach(payrollId => {
        const record = state.records.find(r => r.payroll_id === payrollId)
        if (record) {
          record.status = 'paid'
        }
      })
    },
    // Calculate summary from current records
    calculateSummary: (state) => {
      const records = state.records
      state.summary = {
        totalGrossPay: records.reduce((sum, record) => sum + record.gross_pay, 0),
        totalNetPay: records.reduce((sum, record) => sum + record.net_pay, 0),
        totalTaxDeductions: records.reduce((sum, record) => sum + record.tax_deductions, 0),
        totalEmployees: new Set(records.map(record => record.employee_id)).size,
        averagePay: records.length > 0 ? records.reduce((sum, record) => sum + record.net_pay, 0) / records.length : 0,
      }
    },
  },
})

export const {
  setLoading,
  setError,
  setRecords,
  setCurrentRecord,
  addRecord,
  updateRecord,
  removeRecord,
  setFilters,
  clearFilters,
  setPagination,
  setSummary,
  updateSummary,
  clearError,
  clearCurrentRecord,
  approveRecords,
  markRecordsAsPaid,
  calculateSummary,
} = payrollSlice.actions

export default payrollSlice.reducer
